import FeatureLayer from '@arcgis/core/layers/FeatureLayer'
import ImageryLayer from '@arcgis/core/layers/ImageryLayer'
import TileLayer from '@arcgis/core/layers/TileLayer.js'
import uniq from 'lodash-es/uniq'
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer'

import { DEFAULT_LAYER_OPACITY, MAP_LAYER_IDS, MAX_RECENT_LAYERS } from './constants'
import ghAnalytics from '../lib/analytics'
import { kebabify, sortList, toTitleCase } from '../lib/utils'

export const hasLayers = category => category.layers?.length > 0
export const hasSubCategories = category => category.subCategories?.length > 0
export const setBaseLayersVisiblity = ({ all, baseLayers, ids, visible }) => {
  for (let baseLayer of baseLayers) {
    if (all) {
      baseLayer.visible = visible
    } else {
      baseLayer.visible = ids.includes(baseLayer.id)
    }
  }
}
export const setLayerManifest = (layers, preferences) => {
  let newEventsList = []
  let newStatesList = []

  if (layers.special_events?.length > 0) {
    newEventsList = sortAndSetLayers(layers.special_events, preferences)
  }
  if (layers.states?.length > 0) {
    newStatesList = sortAndSetLayers(layers.states, preferences)
  }
  return {
    ...layers,
    events: sortList(newEventsList, 'stateName', 'string'),
    states: sortList(newStatesList, 'stateName', 'string')
  }
}
export const setLayerPriorityMap = manifest => {
  const { states } = manifest
  const layerPriorityMap = {}
  if (states.length > 0) {
    for (let state of states) {
      const { stateName, categories } = state
      const stateLayerPriorityMap = {}
      for (const category of categories) {
        for (const subCategory of category.subCategories) {
          for (const layer of subCategory.layers) {
            const priorityKey = kebabify(`${stateName} ${category.categoryName} ${subCategory.subCategoryName} ${layer.layerName}`)
            stateLayerPriorityMap[priorityKey] = layer.priority
          }
        }
      }
      layerPriorityMap[stateName] = stateLayerPriorityMap
    }
  }

  return layerPriorityMap
}
export const setMapLayers = ({ events, mapRef, priorityMap, states }) => {
  const layerSets = [events, states]
  const result = {
    added: [],
    errored: [],
    removed: []
  }
  for (let layers of layerSets) {
    for (const state of layers) {
      const layersAdded = []
      let newMapLayer = null
      for (const category of state.categories) {
        if (category.subCategories?.length > 0) {
          for (const subCategory of category.subCategories) {
            if (subCategory.layers != null && subCategory.layers.length >= 1) {
              subCategory.layers.forEach((layer, i) => {
                let hasLayerErrored = false
                const layerId = `${state.stateName}-${category.categoryName}-${subCategory.subCategoryName}-${layer.layerName}`
                const statePriorityMap = priorityMap[state.stateName]
                const layerPriority = statePriorityMap && statePriorityMap[kebabify(layerId)]
                if (layer.control.enabled) {
                  newMapLayer = createNewLayer({
                    id: layerId,
                    opacity: category.opacity,
                    priority: layerPriority,
                    urls: layer.urls
                  })

                  newMapLayer.on('layerview-create-error', e => {
                    hasLayerErrored = true
                    ghAnalytics.logLayerError(e, state, category, subCategory, layer)
                  })
                  if (!hasLayerErrored) {
                    layersAdded.push(newMapLayer)
                    result.added.push(`${state.stateName}|${category.categoryName}|${subCategory.subCategoryName}|${layer.layerName}`)
                  } else {
                    result.errored.push(`${state.stateName}|${category.categoryName}|${subCategory.subCategoryName}|${layer.layerName}`)
                  }
                }
              })
            }
          }
        }
      }
      if (layersAdded.length > 0) {
        const addAfterReferenceLayer = []
        const addBeforeReferenceLayer = []
        const mapLayerItems = mapRef.layers.items
        const referenceContentIndex = mapLayerItems.findIndex(i => i.id === MAP_LAYER_IDS.reference)
        const referenceContentPriority = mapLayerItems[referenceContentIndex]?.priority
        for (let li = 0; li < layersAdded.length; li++) {
          if (layersAdded[li].priority <= referenceContentPriority) {
            addAfterReferenceLayer.push(layersAdded[li])
          } else {
            addBeforeReferenceLayer.push(layersAdded[li])
          }
        }
        sortList(addAfterReferenceLayer, 'priority', 'number', false)
        sortList(addBeforeReferenceLayer, 'priority', 'number', false)
        mapRef.layers.addMany(addAfterReferenceLayer, referenceContentIndex + 1)
        mapRef.layers.addMany(addBeforeReferenceLayer, referenceContentIndex)
      }
    }
  }
  return result
}
export const setOperationalLayers = ({ basemaps, contentConfig }) => {
  const baseLayers = []
  const contentLayers = []
  let baseLayer = null
  let baseLayerId = null
  let contentLayer = null
 
  let basemapLayerParams = null
  for (let basemapConfig of basemaps) {
    baseLayerId = MAP_LAYER_IDS.basemap[basemapConfig.name.toLowerCase()]
    basemapLayerParams = {
      apiKey: basemapConfig.apiKey,
      id: baseLayerId,
      priority: basemapConfig.priority,
      url: basemapConfig.tileServiceUrl,
      visible: true
    }
    try {
      if (baseLayerId !== MAP_LAYER_IDS.basemap.topographic) {
        // layerParams.apiKey = basemapConfig.apiKey
        baseLayer = new TileLayer(basemapLayerParams)
      } else {
        baseLayer = new VectorTileLayer(basemapLayerParams)
      }
    } catch (error) {      
      console.error(toTitleCase(baseLayerId.replace(/-/g, ' ')) + ' retrieval error: ', error)
    }
    if (baseLayer) {
      if (basemapConfig.default) {
        baseLayers.push(baseLayer)
      } else {
        baseLayers.unshift(baseLayer)
      }
    }
  }

  try {
    baseLayer = new TileLayer({
      id: MAP_LAYER_IDS.hillshade,
      priority: contentConfig.hillshade.priority,
      url: contentConfig.hillshade.url,
      visible: false
    })
  } catch (error) {
    console.error(`${toTitleCase(MAP_LAYER_IDS.contour.replace(/-/g, ' '))} retrieval error: `, error)
  }

  if (baseLayer) baseLayers.unshift(baseLayer)

  try {
    contentLayer = new VectorTileLayer({
      id: MAP_LAYER_IDS.contour,
      priority: contentConfig.contour.priority,
      url: contentConfig.contour.url,
      visible: false
    })
  } catch (error) {
    console.error(`${toTitleCase(MAP_LAYER_IDS.contour.replace(/-/g, ' '))} retrieval error: `, error)
  }

  if (contentLayer) contentLayers.push(contentLayer)

  try {
    contentLayer = new VectorTileLayer({
      id: MAP_LAYER_IDS.reference,
      priority: contentConfig.reference.priority,
      url: contentConfig.reference.url
    })
  } catch (error) {
    console.error(`${toTitleCase(MAP_LAYER_IDS.contour.replace(/-/g, ' '))} retrieval error: `, error)
  }

  if (contentLayer) contentLayers.push(contentLayer)

  return {
    baseLayers,
    contentLayers
  }

}
export const updateMapLayers = ({ all, layers, mapRef, priorityMap, status }) => {
  const layerKeys = []
  const newlayers = []
  const referenceContentIndex = mapRef.layers.items.findIndex(i => i.id === MAP_LAYER_IDS.reference)
  if (all) {
    for (const layer of layers) {
      const { categoryName, layerName, stateName, subCategoryName } = layer
      const layerKey = `${stateName}|${categoryName}|${subCategoryName}|${layerName}`
      const mapLayerId = `${stateName}-${categoryName}-${subCategoryName}-${layerName}`
      const mapLayer = mapRef.findLayerById(mapLayerId)
      const statePriorityMap = priorityMap[stateName]

      if (mapLayer) {
        mapLayer.visible = status
        layerKeys.push(layerKey)
      } else {
        const layerPriority = statePriorityMap && statePriorityMap[kebabify(mapLayerId)]
        const newMapLayer = createNewLayer({
          id: mapLayerId,
          opacity: layer.opacity,
          priority: layerPriority,
          urls: layer.urls,
          visible: status
        })
        newMapLayer.on('layerview-create-error', e => {
          ghAnalytics.logLayerError(e, { stateName }, { categoryName }, { subCategoryName }, layer)
        })
        if (newMapLayer?.id) {
          layerKeys.push(layerKey)
          newlayers.push(newMapLayer)
        }
      }
    }
    if (newlayers.length > 0) {
      const addBeforeReferenceLayer = []
      const addAfterReferenceLayer = []
      const referenceContentPriority = mapRef.layers.items[referenceContentIndex]?.priority
      sortList(newlayers, 'priority', 'number', false)
      for (let li = 0; li < newlayers.length; li++) {
        if (newlayers[li].priority <= referenceContentPriority) {
          addAfterReferenceLayer.push(newlayers[li])
        } else {
          addBeforeReferenceLayer.push(newlayers[li])
        }
      }
      mapRef.layers.addMany(addAfterReferenceLayer, referenceContentIndex + 1)
      mapRef.layers.addMany(addBeforeReferenceLayer, referenceContentIndex)
    }
  } else {
    for (const layer of layers) {
      const { control, categoryName, layerName, stateName, subCategoryName } = layer
      const layerKey = `${stateName}|${categoryName}|${subCategoryName}|${layerName}`
      const mapLayerId = `${stateName}-${categoryName}-${subCategoryName}-${layerName}`
      const mapLayer = mapRef.findLayerById(mapLayerId)
      const statePriorityMap = priorityMap[stateName]
      const layerPriority = statePriorityMap && statePriorityMap[kebabify(mapLayerId)]

      if (mapLayer) {
        mapLayer.visible = control.enabled
        layerKeys.push(layerKey)
      } else {
        const newMapLayer = createNewLayer({
          id: mapLayerId,
          opacity: layer.opacity,
          priority: layerPriority,
          urls: layer.urls,
          visible: control.enabled
        })

        newMapLayer.on('layerview-create-error', e => {
          ghAnalytics.logLayerError(e, { stateName }, { categoryName }, { subCategoryName }, layer)
        })
        if (newMapLayer?.id) {
          layerKeys.push(layerKey)
          const mapLayerItems = mapRef.layers.items
          const mapLayerItemsLength = mapLayerItems.length
          let insertIndex = referenceContentIndex

          if (newMapLayer.priority < 7) {
            insertIndex += 1
            for (let li = mapLayerItemsLength - 1; li >= 0; li--) {
              if (mapLayerItems[li].priority >= newMapLayer.priority) {
                insertIndex = li + 1
                break
              }
            }
          } else {
            for (let li = 0; li < mapLayerItemsLength; li++) {
              if (mapLayerItems[li].priority <= newMapLayer.priority) {
                insertIndex = li
                break
              }
            }
          }
          mapRef.layers.add(newMapLayer, insertIndex)
        }
      }
    }
  }
  return layerKeys
}
export const updateMapLayersOpacity = ({ layerIds, opacity, mapRef }) => {
  for (let layerId of layerIds) {
    const mapLayer = mapRef.findLayerById(layerId)
    if (mapLayer) {
      mapLayer.opacity = opacity
    }
  }
}

/******************
 * Helpers
 ******************/
function addLayerControls(stateObj, category, subCategory, preferences) {
  if (!subCategory || subCategory == null) {
    return null
  } else {
    let newLayer = null
    return subCategory.layers.map(layer => {
      const compositeKey = `${stateObj.stateName}|${category.categoryName}|${subCategory.subCategoryName}|${layer.layerName}`
      if (preferences.active.indexOf(compositeKey) !== -1) {
        newLayer = {
          ...layer,
          control: {
            enabled: true
          }
        }
      } else {
        newLayer = {
          ...layer,
          control: {
            enabled: false
          }
        }
      }
      return newLayer
    })
  }
}
function createNewLayer({ id, opacity = DEFAULT_LAYER_OPACITY, outFields = [], priority, urls }) {
  const { raster, vectorTile, mainRes } = urls
  let newLayer = null
  let params = { id, opacity, priority }
  if (raster != null) {
    params = {
      ...params,
      url: raster
    }
    newLayer = new ImageryLayer(params)
  } else if (vectorTile != null) {
    params = {
      ...params,
      url: vectorTile
    }
    newLayer = new VectorTileLayer(params)
  } else {
    params = {
      ...params,
      elevationInfo: { mode: 'on-the-ground' },
      outFields,
      url: mainRes
    }
    newLayer = new FeatureLayer(params)
  }
  return newLayer
}
function getLayersState(compositeKey, visibility, preferences) {
  let { active, recent } = preferences

  if (visibility) {
    active = uniq([...active, compositeKey])
  } else {
    let index = active.indexOf(compositeKey)
    if (index !== -1) {
      active.splice(index, 1)
    }
  }

  const index = recent.indexOf(compositeKey)
  if (visibility) {
    // turn a layer on, remove from recent
    if (index !== -1) {
      recent.splice(index, 1)
    }
  } else {
    // turn a layer off, add to recent
    if (index == -1) {
      recent.push(compositeKey)
    } else {
      recent.push(recent.splice(index, 1)[0])
    }

    if (recent.length > MAX_RECENT_LAYERS) {
      recent.shift()
    }
  }

  return {
    active,
    recent
  }
}
function getMultipleLayersState(layers, preferences) {
  let { active, recent } = preferences

  layers.forEach(layer => {
    const { stateName, categoryName, subCategoryName, layerName, status } = layer
    const compositeKey = `${stateName}|${categoryName}|${subCategoryName}|${layerName}`
    if (status) {
      active = uniq([...active, compositeKey])
    } else {
      let index = active.indexOf(compositeKey)
      if (index !== -1) {
        active.splice(index, 1)
      }
    }

    const index = recent.indexOf(compositeKey)
    if (status) {
      // turn a layer on, remove from recent
      if (index !== -1) {
        recent.splice(index, 1)
      }
    } else {
      // turn a layer off, add to recent
      if (index == -1) {
        recent.push(compositeKey)
      } else {
        recent.push(recent.splice(index, 1)[0])
      }

      if (recent.length > MAX_RECENT_LAYERS) {
        recent.shift()
      }
    }
  })

  return {
    active,
    recent
  }
}
function sortAndSetLayers(layersArray, preferences) {
  return layersArray.map(layer => {
    if (layer.categoryCount > 0 && layer.categories?.length > 0) {
      sortList(layer.categories, 'categoryName', 'string')
      return {
        ...layer,
        categories: layer.categories.map(category => {
          /*
            Since there are multiple layer types being displayed,
            which are events and states, we need the ability to
            disable a layer's category based off a key, which is
            "isPublished". It is only attached to the event layers
            at this time. For the time being, the "isPublished"
            key is being added to all of the states categories if
            it doesn't already exist and will default to true. 
            This allows us to easily maintain layer functionality.
              - May 10th, 2022  
          */
          if ([null, undefined].includes(category.isPublished)) {
            category.isPublished = true
          }
          if (hasSubCategories(category)) {
            let subCategoryLayers = undefined
            category = {
              ...category,
              subCategories: category.subCategories.map(subCategory => {
                if (hasLayers(subCategory)) {
                  subCategoryLayers = addLayerControls(layer, category, subCategory, preferences)
                  if (subCategoryLayers?.length > 0) {
                    const sortBy = subCategoryLayers.every(l => /[\d]/g.test(l.layerName)) ? 'number' : 'string'
                    sortList(subCategoryLayers, 'layerName', sortBy)
                  }
                }
                return {
                  ...subCategory,
                  layers: subCategoryLayers
                }
              })
            }
          }
          const key = `${layer.stateName}-${category.categoryName}`
          category.opacity = preferences.category.opacity[key] || DEFAULT_LAYER_OPACITY
          return category
        })
      }
    } else {
      return layer
    }
  })
}
